import type { HotelScript, VoiceConfig } from '@hostesa/api-client';
import {
  useApproveScript,
  usePutScript,
  useScriptVersionsByLanguage,
  useSetFallbackLanguage,
  useSetHandoffRules,
  useVoiceConfig,
} from '@hostesa/api-client';
import { useAuth } from '@hostesa/auth-client';
import { useT } from '@hostesa/i18n';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorState,
  Input,
  Label,
  LoadingState,
  PageHeader,
  StatusBadge,
  Switch,
  Textarea,
} from '@hostesa/ui';
import { useForm } from '@tanstack/react-form';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { useHotelContext } from '../../lib/hotel-context';

/** BCP-47 language tags the Script Studio supports, in tab order. */
const LANGUAGES = ['bg', 'en', 'de', 'fr'] as const;
type Language = (typeof LANGUAGES)[number];

/** All eleven script fields, in display order; the first four are required. */
const SCRIPT_FIELDS = [
  'greeting',
  'ai_disclosure',
  'recording_notice',
  'reservation_questions',
  'no_availability',
  'price_unknown',
  'existing_booking',
  'human_handoff',
  'complaint',
  'emergency',
  'call_closing',
] as const;
type ScriptField = (typeof SCRIPT_FIELDS)[number];

const REQUIRED_FIELDS = new Set<ScriptField>([
  'greeting',
  'ai_disclosure',
  'human_handoff',
  'call_closing',
]);

/** Soft character ceiling above which scripts risk sounding unnatural. */
const SOFT_CHAR_LIMIT = 300;

type FieldStatus = 'approved' | 'draft' | 'notSet';

/** Version history per field is newest-first, so the head is the latest version. */
type VersionsByField = Record<string, readonly HotelScript[]>;

/**
 * The latest version wins — a newer draft beats an older approved row. This is the
 * script the studio shows and edits, and (if a draft) the one the Approve button targets.
 */
function latestVersion(versions: readonly HotelScript[] | undefined): HotelScript | undefined {
  return versions?.[0];
}

function statusOf(script: HotelScript | undefined): FieldStatus {
  if (!script) return 'notSet';
  return script.status === 'approved' ? 'approved' : 'draft';
}

/**
 * Required-for-live check: the runtime uses the highest-version APPROVED script, so a field
 * satisfies the requirement as soon as ANY approved version exists — even if a newer draft
 * is pending (which the field list still surfaces as "Draft" via {@link latestVersion}).
 */
function hasApprovedVersion(versions: readonly HotelScript[] | undefined): boolean {
  return (versions ?? []).some((s) => s.status === 'approved');
}

// ── Script field list (left panel) ───────────────────────────────────────────

interface ScriptVersionListProps {
  versionsByField: VersionsByField;
  selectedField: ScriptField;
  onSelect: (field: ScriptField) => void;
}

function ScriptVersionList({ versionsByField, selectedField, onSelect }: ScriptVersionListProps) {
  const { t } = useT('scripts');

  return (
    <ul data-testid="script-field-list" className="flex flex-col gap-1">
      {SCRIPT_FIELDS.map((field) => {
        const status = statusOf(latestVersion(versionsByField[field]));
        const isRequired = REQUIRED_FIELDS.has(field);
        const isSelected = field === selectedField;
        const variant =
          status === 'approved' ? 'success' : status === 'draft' ? 'warning' : 'neutral';
        return (
          <li key={field}>
            <button
              type="button"
              data-testid={`script-field-${field}`}
              aria-current={isSelected}
              onClick={() => onSelect(field)}
              className={`flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm ${
                isSelected
                  ? 'bg-[var(--color-canvas)] font-medium text-[var(--color-charcoal)]'
                  : 'text-[var(--color-stone-mid)] hover:bg-[var(--color-canvas)]'
              }`}
            >
              <span className="flex items-center gap-1">
                {t(`fields.${field}`)}
                {isRequired && (
                  <span aria-hidden="true" className="text-[var(--color-alert)]">
                    *
                  </span>
                )}
              </span>
              <StatusBadge
                data-testid={`script-field-status-${field}`}
                status={variant}
                label={t(`fieldStatus.${status}`)}
              />
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// ── Script editor (right panel) ──────────────────────────────────────────────

interface ScriptEditorProps {
  tenantId: string;
  hotelId: string;
  language: Language;
  field: ScriptField;
  script: HotelScript | undefined;
  canApprove: boolean;
}

function ScriptEditor({
  tenantId,
  hotelId,
  language,
  field,
  script,
  canApprove,
}: ScriptEditorProps) {
  const { t } = useT('scripts');
  const putScript = usePutScript(tenantId, hotelId);
  const approveScript = useApproveScript(tenantId, hotelId);

  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);

  const status = statusOf(script);
  const isLive = status === 'approved';

  const contentSchema = z.string().min(1, t('editor.contentRequired'));

  const form = useForm({
    defaultValues: { content: script?.content ?? '' },
    onSubmit: async ({ value }) => {
      setSaveError(null);
      setSaved(false);
      try {
        await putScript.mutateAsync({ language, field, content: value.content });
        setSaved(true);
      } catch {
        setSaveError(t('editor.saveError'));
      }
    },
  });

  async function handleApprove() {
    if (!script) return;
    setApproveError(null);
    try {
      // High-stakes, NON-optimistic: await the backend before reflecting state.
      await approveScript.mutateAsync({ scriptId: script.id });
    } catch {
      setApproveError(t('editor.approveError'));
    }
  }

  const approvedBy =
    typeof script?.approvedBy === 'string' && script.approvedBy.length > 0
      ? String(script.approvedBy)
      : null;

  return (
    <Card data-testid="script-editor">
      <CardHeader>
        <CardTitle>{t(`fields.${field}`)}</CardTitle>
        <StatusBadge
          data-testid="script-editor-live-status"
          status={isLive ? 'success' : 'neutral'}
          label={isLive ? t('studio.live') : t('studio.notLive')}
        />
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {isLive && (
          <p
            data-testid="script-editor-live-warning"
            role="status"
            className="rounded-md border border-[var(--color-warm-stone-deep)] bg-[var(--color-canvas)] px-3 py-2 text-sm text-[var(--color-warm-stone-deep)]"
          >
            {t('editor.liveWarning')}
          </p>
        )}

        <form
          data-testid="script-editor-form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
          className="flex flex-col gap-3"
        >
          <form.Field
            name="content"
            validators={{ onSubmit: contentSchema }}
            // biome-ignore lint/correctness/noChildrenProp: TanStack Form field render-prop API
            children={(fieldApi) => {
              const count = fieldApi.state.value.length;
              return (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="script-content">{t('editor.contentLabel')}</Label>
                  <Textarea
                    id="script-content"
                    data-testid="script-content-input"
                    rows={6}
                    placeholder={t('editor.contentPlaceholder')}
                    value={fieldApi.state.value}
                    onBlur={fieldApi.handleBlur}
                    onChange={(e) => {
                      setSaved(false);
                      fieldApi.handleChange(e.target.value);
                    }}
                    aria-invalid={fieldApi.state.meta.errors.length > 0}
                  />
                  <div className="flex items-center justify-between text-xs text-[var(--color-stone-mid)]">
                    <span data-testid="script-content-count">
                      {t('editor.charCount', { count })}
                    </span>
                    {count > SOFT_CHAR_LIMIT && (
                      <span
                        data-testid="script-content-warning"
                        role="status"
                        className="text-[var(--color-warm-stone-deep)]"
                      >
                        {t('editor.charWarning')}
                      </span>
                    )}
                  </div>
                  {fieldApi.state.meta.errors.length > 0 && (
                    <p
                      data-testid="script-content-error"
                      role="alert"
                      className="text-xs text-[var(--color-alert)]"
                    >
                      {String(
                        fieldApi.state.meta.errors[0]?.message ?? fieldApi.state.meta.errors[0],
                      )}
                    </p>
                  )}
                </div>
              );
            }}
          />

          {saveError && (
            <p
              data-testid="script-save-error"
              role="alert"
              className="text-sm text-[var(--color-alert)]"
            >
              {saveError}
            </p>
          )}
          {saved && (
            <p
              data-testid="script-save-success"
              role="status"
              className="text-sm text-[var(--color-success)]"
            >
              {t('editor.saved')}
            </p>
          )}

          <form.Subscribe
            selector={(state) => state.isSubmitting}
            // biome-ignore lint/correctness/noChildrenProp: TanStack Form Subscribe render-prop API
            children={(isSubmitting) => (
              <Button type="submit" data-testid="script-save-btn" disabled={isSubmitting}>
                {isSubmitting ? t('editor.saving') : t('editor.saveDraft')}
              </Button>
            )}
          />
        </form>

        <p className="text-xs text-[var(--color-stone-mid)]">{t('editor.notApprovedNotice')}</p>

        {/* Approval — high-stakes, gated, and audited. */}
        <div className="flex flex-col gap-2 border-t border-[var(--color-warm-stone)] pt-3">
          {approveError && (
            <p
              data-testid="script-approve-error"
              role="alert"
              className="text-sm text-[var(--color-alert)]"
            >
              {approveError}
            </p>
          )}
          {isLive && approvedBy && (
            <p data-testid="script-approved-by" className="text-xs text-[var(--color-stone-mid)]">
              {t('editor.approvedBy', { name: approvedBy })}
            </p>
          )}
          {canApprove ? (
            <Button
              type="button"
              variant="secondary"
              data-testid="script-approve-btn"
              disabled={!script || status !== 'draft' || approveScript.isPending}
              onClick={() => void handleApprove()}
            >
              {approveScript.isPending ? t('editor.approving') : t('editor.approve')}
            </Button>
          ) : (
            <p
              data-testid="script-approve-disabled-hint"
              className="text-xs text-[var(--color-stone-mid)]"
            >
              {t('permissions.approveDisabledHint')}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Fallback language control ────────────────────────────────────────────────

interface FallbackLanguageControlProps {
  tenantId: string;
  hotelId: string;
  current: string;
}

/**
 * Sets the language calls fall back to when the caller's language has no approved scripts.
 * NON-optimistic: the control reflects the server value and only moves once the PUT resolves
 * (the success handler refreshes the voice-config cache).
 */
function FallbackLanguageControl({ tenantId, hotelId, current }: FallbackLanguageControlProps) {
  const { t } = useT('scripts');
  const setFallback = useSetFallbackLanguage(tenantId, hotelId);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="flex items-center gap-2" data-testid="scripts-fallback">
      <Label htmlFor="scripts-fallback-select" className="text-xs text-[var(--color-stone-mid)]">
        {t('studio.fallbackLabel')}
      </Label>
      <select
        id="scripts-fallback-select"
        data-testid="scripts-fallback-select"
        value={current}
        disabled={setFallback.isPending}
        title={t('studio.fallbackHint')}
        onChange={(e) => {
          const language = e.target.value as Language;
          setError(null);
          setFallback.mutate({ language }, { onError: () => setError(t('studio.fallbackError')) });
        }}
        className="h-9 rounded-md border border-[var(--color-input)] bg-[var(--color-alabaster)] px-2 text-sm uppercase text-[var(--color-charcoal)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] disabled:cursor-not-allowed disabled:opacity-50"
      >
        {LANGUAGES.map((lang) => (
          <option key={lang} value={lang}>
            {lang.toUpperCase()}
          </option>
        ))}
      </select>
      {error && (
        <p
          data-testid="scripts-fallback-error"
          role="alert"
          className="text-xs text-[var(--color-alert)]"
        >
          {error}
        </p>
      )}
    </div>
  );
}

// ── Script Studio page ───────────────────────────────────────────────────────

export function ScriptsPage() {
  const { t } = useT('scripts');
  const hotel = useHotelContext();
  const { can } = useAuth();
  const voiceConfig = useVoiceConfig(hotel.tenantId, hotel.hotelId);
  const [language, setLanguage] = useState<Language>('bg');
  const [selectedField, setSelectedField] = useState<ScriptField>('greeting');

  const canApprove = can('hotel:configure');
  const canConfigure = can('hotel:configure');

  // TRUE statuses come from the per-field version history — the approved-only
  // /voice-config payload omits drafts entirely, so it can't be the source of status.
  const versions = useScriptVersionsByLanguage(
    hotel.tenantId,
    hotel.hotelId,
    language,
    SCRIPT_FIELDS,
  );
  const selectedScript = latestVersion(versions.byField[selectedField]);

  const missingRequired = useMemo(() => {
    return Array.from(REQUIRED_FIELDS).some(
      (field) => !hasApprovedVersion(versions.byField[field]),
    );
  }, [versions.byField]);

  return (
    <div data-testid="scripts-page" className="flex flex-col gap-6">
      <PageHeader
        eyebrow={t('eyebrow')}
        title={t('studio.title')}
        description={t('studio.description')}
      />

      {voiceConfig.isLoading && (
        <LoadingState data-testid="scripts-loading" title={t('states.loading')} />
      )}

      {voiceConfig.isError && (
        <ErrorState
          data-testid="scripts-error"
          title={t('states.errorTitle')}
          description={t('states.errorDescription')}
          action={
            <Button
              type="button"
              data-testid="scripts-retry-btn"
              onClick={() => void voiceConfig.refetch()}
            >
              {t('states.retry')}
            </Button>
          }
        />
      )}

      {voiceConfig.isSuccess && (
        <>
          {missingRequired && (
            <div
              data-testid="scripts-required-banner"
              role="status"
              className="rounded-md border border-[var(--color-alert)] bg-[var(--color-canvas)] px-4 py-3"
            >
              <p className="text-sm font-medium text-[var(--color-charcoal)]">
                {t('required.bannerTitle')}
              </p>
              <p className="text-sm text-[var(--color-stone-mid)]">
                {t('required.bannerDescription')}
              </p>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div
              data-testid="scripts-language-tabs"
              role="tablist"
              aria-label={t('studio.languageLabel')}
              className="flex flex-wrap items-center gap-2"
            >
              {LANGUAGES.map((lang) => (
                <button
                  key={lang}
                  type="button"
                  role="tab"
                  aria-selected={lang === language}
                  data-testid={`scripts-language-tab-${lang}`}
                  onClick={() => setLanguage(lang)}
                  className={`rounded-md px-3 py-1.5 text-sm uppercase ${
                    lang === language
                      ? 'bg-[var(--color-charcoal)] text-[var(--color-canvas)]'
                      : 'bg-[var(--color-canvas)] text-[var(--color-stone-mid)]'
                  }`}
                >
                  {lang}
                </button>
              ))}
            </div>

            {canConfigure && (
              <FallbackLanguageControl
                tenantId={hotel.tenantId}
                hotelId={hotel.hotelId}
                current={voiceConfig.data.fallbackLanguage}
              />
            )}
          </div>

          <div className="grid gap-6 lg:grid-cols-[20rem_1fr]">
            <Card data-testid="scripts-field-panel">
              <CardHeader>
                <CardTitle>{t('studio.fieldsHeading')}</CardTitle>
              </CardHeader>
              <CardContent>
                <ScriptVersionList
                  versionsByField={versions.byField}
                  selectedField={selectedField}
                  onSelect={setSelectedField}
                />
              </CardContent>
            </Card>

            <ScriptEditor
              // Remount when the selected field OR its latest version changes, so
              // the editor form reloads the freshest content (e.g. after a save
              // creates a new draft version) rather than keeping stale defaults.
              key={`${language}:${selectedField}:${selectedScript?.id ?? 'new'}`}
              tenantId={hotel.tenantId}
              hotelId={hotel.hotelId}
              language={language}
              field={selectedField}
              script={selectedScript}
              canApprove={canApprove}
            />
          </div>
        </>
      )}
    </div>
  );
}

// ── Handoff rules editor ─────────────────────────────────────────────────────

interface HandoffRules {
  transferTarget: string;
  maxWaitSeconds: number | null;
  transferOnFrustration: boolean;
  transferOnPayment: boolean;
}

function readHandoffRules(config: VoiceConfig | undefined): HandoffRules {
  const raw = (config?.handoffRules ?? {}) as Record<string, unknown>;
  const target = typeof raw['transferTarget'] === 'string' ? raw['transferTarget'] : '';
  const maxWait = typeof raw['maxWaitSeconds'] === 'number' ? raw['maxWaitSeconds'] : null;
  return {
    transferTarget: target,
    maxWaitSeconds: maxWait,
    transferOnFrustration: raw['transferOnFrustration'] === true,
    transferOnPayment: raw['transferOnPayment'] === true,
  };
}

interface HandoffRulesEditorProps {
  tenantId: string;
  hotelId: string;
  config: VoiceConfig;
  canConfigure: boolean;
}

function HandoffRulesEditor({ tenantId, hotelId, config, canConfigure }: HandoffRulesEditorProps) {
  const { t } = useT('scripts');
  const setHandoffRules = useSetHandoffRules(tenantId, hotelId);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const initial = readHandoffRules(config);
  const phoneSchema = z
    .string()
    .min(1, t('handoff.transferTargetInvalid'))
    .regex(/^\+?[0-9]{6,15}$/, t('handoff.transferTargetInvalid'));
  const maxWaitSchema = z
    .string()
    .refine(
      (v) => v === '' || (/^\d+$/.test(v) && Number(v) >= 0 && Number(v) <= 600),
      t('handoff.maxWaitInvalid'),
    );

  const form = useForm({
    defaultValues: {
      transferTarget: initial.transferTarget,
      maxWaitSeconds: initial.maxWaitSeconds === null ? '' : String(initial.maxWaitSeconds),
      transferOnFrustration: initial.transferOnFrustration,
      transferOnPayment: initial.transferOnPayment,
    },
    onSubmit: async ({ value }) => {
      setSaveError(null);
      setSaved(false);
      const rules: Record<string, unknown> = {
        transferTarget: value.transferTarget,
        transferOnFrustration: value.transferOnFrustration,
        transferOnPayment: value.transferOnPayment,
      };
      if (value.maxWaitSeconds !== '') {
        rules['maxWaitSeconds'] = Number(value.maxWaitSeconds);
      }
      try {
        // NON-optimistic provider configuration: await the backend.
        await setHandoffRules.mutateAsync({ rules } as never);
        setSaved(true);
      } catch {
        setSaveError(t('handoff.saveError'));
      }
    },
  });

  return (
    <form
      data-testid="handoff-rules-form"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void form.handleSubmit();
      }}
      className="flex flex-col gap-4"
    >
      <form.Field
        name="transferTarget"
        validators={{ onSubmit: phoneSchema }}
        // biome-ignore lint/correctness/noChildrenProp: TanStack Form field render-prop API
        children={(fieldApi) => (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="handoff-transfer-target">{t('handoff.transferTargetLabel')}</Label>
            <Input
              id="handoff-transfer-target"
              data-testid="handoff-transfer-target-input"
              type="tel"
              placeholder={t('handoff.transferTargetPlaceholder')}
              value={fieldApi.state.value}
              onBlur={fieldApi.handleBlur}
              onChange={(e) => fieldApi.handleChange(e.target.value)}
              aria-invalid={fieldApi.state.meta.errors.length > 0}
              disabled={!canConfigure}
            />
            {fieldApi.state.meta.errors.length > 0 && (
              <p
                data-testid="handoff-transfer-target-error"
                role="alert"
                className="text-xs text-[var(--color-alert)]"
              >
                {String(fieldApi.state.meta.errors[0]?.message ?? fieldApi.state.meta.errors[0])}
              </p>
            )}
          </div>
        )}
      />

      <form.Field
        name="maxWaitSeconds"
        validators={{ onSubmit: maxWaitSchema }}
        // biome-ignore lint/correctness/noChildrenProp: TanStack Form field render-prop API
        children={(fieldApi) => (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="handoff-max-wait">{t('handoff.maxWaitLabel')}</Label>
            <Input
              id="handoff-max-wait"
              data-testid="handoff-max-wait-input"
              inputMode="numeric"
              placeholder={t('handoff.maxWaitPlaceholder')}
              value={fieldApi.state.value}
              onBlur={fieldApi.handleBlur}
              onChange={(e) => fieldApi.handleChange(e.target.value)}
              aria-invalid={fieldApi.state.meta.errors.length > 0}
              disabled={!canConfigure}
            />
            {fieldApi.state.meta.errors.length > 0 && (
              <p
                data-testid="handoff-max-wait-error"
                role="alert"
                className="text-xs text-[var(--color-alert)]"
              >
                {String(fieldApi.state.meta.errors[0]?.message ?? fieldApi.state.meta.errors[0])}
              </p>
            )}
          </div>
        )}
      />

      <form.Field
        name="transferOnFrustration"
        // biome-ignore lint/correctness/noChildrenProp: TanStack Form field render-prop API
        children={(fieldApi) => (
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="handoff-frustration">{t('handoff.transferOnFrustrationLabel')}</Label>
            <Switch
              id="handoff-frustration"
              data-testid="handoff-frustration-switch"
              checked={fieldApi.state.value}
              onCheckedChange={(v) => fieldApi.handleChange(v)}
              disabled={!canConfigure}
            />
          </div>
        )}
      />

      <form.Field
        name="transferOnPayment"
        // biome-ignore lint/correctness/noChildrenProp: TanStack Form field render-prop API
        children={(fieldApi) => (
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="handoff-payment">{t('handoff.transferOnPaymentLabel')}</Label>
            <Switch
              id="handoff-payment"
              data-testid="handoff-payment-switch"
              checked={fieldApi.state.value}
              onCheckedChange={(v) => fieldApi.handleChange(v)}
              disabled={!canConfigure}
            />
          </div>
        )}
      />

      {saveError && (
        <p
          data-testid="handoff-save-error"
          role="alert"
          className="text-sm text-[var(--color-alert)]"
        >
          {saveError}
        </p>
      )}
      {saved && (
        <p
          data-testid="handoff-save-success"
          role="status"
          className="text-sm text-[var(--color-success)]"
        >
          {t('handoff.saved')}
        </p>
      )}

      {canConfigure && (
        <form.Subscribe
          selector={(state) => state.isSubmitting}
          // biome-ignore lint/correctness/noChildrenProp: TanStack Form Subscribe render-prop API
          children={(isSubmitting) => (
            <Button type="submit" data-testid="handoff-save-btn" disabled={isSubmitting}>
              {isSubmitting ? t('handoff.saving') : t('handoff.save')}
            </Button>
          )}
        />
      )}
    </form>
  );
}

export function HandoffRulesPage() {
  const { t } = useT('scripts');
  const hotel = useHotelContext();
  const { can } = useAuth();
  const voiceConfig = useVoiceConfig(hotel.tenantId, hotel.hotelId);
  const canConfigure = can('hotel:configure');

  return (
    <div data-testid="handoff-rules-page" className="flex flex-col gap-6">
      <PageHeader
        eyebrow={t('handoff.eyebrow')}
        title={t('handoff.title')}
        description={t('handoff.description')}
      />

      {voiceConfig.isLoading && (
        <LoadingState data-testid="handoff-loading" title={t('handoff.loading')} />
      )}

      {voiceConfig.isError && (
        <ErrorState
          data-testid="handoff-error"
          title={t('handoff.errorTitle')}
          description={t('handoff.errorDescription')}
          action={
            <Button
              type="button"
              data-testid="handoff-retry-btn"
              onClick={() => void voiceConfig.refetch()}
            >
              {t('states.retry')}
            </Button>
          }
        />
      )}

      {voiceConfig.isSuccess && (
        <Card data-testid="handoff-rules-card">
          <CardContent className="pt-6">
            <HandoffRulesEditor
              tenantId={hotel.tenantId}
              hotelId={hotel.hotelId}
              config={voiceConfig.data}
              canConfigure={canConfigure}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
